/**
 * XSS protection for generated HTML (contracts, invoices, NARSA, print pages,
 * PDFs). User-supplied values (client names, CIN, notes, reservation fields…)
 * are inserted into HTML templates via string interpolation, so they MUST be
 * escaped or a value like `<img src=x onerror=...>` would execute when the
 * document is printed/opened.
 */

/** Escape a single value for safe insertion into HTML text or a quoted attribute. */
export const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Deep-copy an object/array with every STRING value HTML-escaped. Numbers,
 * booleans and null are left as-is. Keys listed in `skip` are passed through
 * untouched (used for fields escaped elsewhere, e.g. `damages` whose notes are
 * escaped by the carDamage renderer — avoids double-escaping).
 *
 * data: URLs and storage URLs (used in <img src>) only contain base64 / URL
 * characters, so escaping leaves them valid (and correct for attributes).
 */
export const escapeForHtml = (input, skip = ['damages']) => {
  if (input === null || input === undefined) return input;
  if (typeof input === 'string') return escapeHtml(input);
  if (typeof input !== 'object') return input; // number, boolean
  if (Array.isArray(input)) return input.map((v) => escapeForHtml(v, skip));
  const out = {};
  for (const [k, v] of Object.entries(input)) {
    out[k] = skip.includes(k) ? v : escapeForHtml(v, skip);
  }
  return out;
};

// Control characters (0x00-0x1F and 0x7F) — built via RegExp ctor so the source
// stays pure ASCII (no embedded control bytes).
const CONTROL_CHARS = new RegExp('[\\u0000-\\u001F\\u007F]', 'g');

/** Light input sanitizer for free-text fields: strip control chars + trim + cap length. */
export const sanitizeText = (value, maxLen = 500) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(CONTROL_CHARS, '').trim().slice(0, maxLen);
};
